/// <reference types="google.maps" />
import { useEffect, useState } from "react";

const SCRIPT_ID = "google-maps-places-script";
const SCRIPT_LOAD_ERROR_MESSAGE = "Failed to load Google Maps script";

type GoogleMapsGlobal = typeof google;

export const getGoogleMapsGlobal = (): GoogleMapsGlobal | undefined =>
  (window as unknown as { google?: GoogleMapsGlobal }).google;

let scriptLoadPromise: Promise<void> | null = null;

const loadGoogleMapsScript = (
  apiKey: string,
  language?: string,
  region?: string
): Promise<void> => {
  if (getGoogleMapsGlobal()?.maps?.places) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () =>
        reject(new Error(SCRIPT_LOAD_ERROR_MESSAGE))
      );
      return;
    }

    const params = new URLSearchParams({
      key: apiKey,
      libraries: "places",
      ...(language ? { language } : {}),
      ...(region ? { region } : {}),
    });

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(SCRIPT_LOAD_ERROR_MESSAGE));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

export interface UseGoogleMapsScriptOptions {
  language?: string;
  region?: string;
}

/**
 * Loads the Google Maps JS SDK (places library) once per page, mirroring the
 * <script src="maps.googleapis.com/maps/api/js?libraries=places"> tag the legacy
 * task-creation flow renders in shared/_javascript.html.haml — done here via a
 * dynamically injected script instead of a Rails-rendered <script> tag.
 */
export const useGoogleMapsScript = (
  apiKey?: string,
  options?: UseGoogleMapsScriptOptions
) => {
  const [isLoaded, setIsLoaded] = useState(() => !!getGoogleMapsGlobal()?.maps?.places);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!apiKey || isLoaded) return undefined;

    let isMounted = true;
    loadGoogleMapsScript(apiKey, options?.language, options?.region)
      .then(() => {
        if (isMounted) setIsLoaded(true);
      })
      .catch((loadError: Error) => {
        if (isMounted) setError(loadError);
      });

    return () => {
      isMounted = false;
    };
  }, [apiKey]);

  return { isLoaded, error };
};
